import { createTheme, alpha } from "@mui/material/styles";

// ── Shared Brand Tokens ────────────────────────────────────────────────────────
const shared = {
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
    button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.01em" },
  },
};

// ── Helper: build common component overrides given a set of brand tokens ───────
function buildComponents({ primary, secondary, grad, gradHover, shadowBrand, navBg, dimPrimary, dimSecondary }) {
  return {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: `0 1px 4px rgba(0,0,0,0.06), 0 2px 12px ${primary}22`,
          border: "1px solid #E2E6F0",
        },
        elevation2: {
          boxShadow: `0 4px 20px rgba(0,0,0,0.08), 0 2px 16px ${primary}30`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid #E2E6F0",
          boxShadow: `0 1px 4px rgba(0,0,0,0.06), 0 2px 12px ${primary}22`,
          backgroundImage: "none",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: navBg,
          color: "#FFFFFF",
          boxShadow: "none",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: navBg,
          color: "#FFFFFF",
          borderRight: "1px solid rgba(255,255,255,0.07)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: "none",
          transition: "all 0.18s ease",
        },
        containedPrimary: {
          background: grad,
          color: "#FFFFFF",
          boxShadow: shadowBrand,
          "&:hover": {
            background: gradHover,
            transform: "translateY(-1px)",
          },
          "&:active": { transform: "translateY(0)" },
        },
        containedSecondary: {
          backgroundColor: secondary,
          boxShadow: `0 3px 14px ${secondary}55`,
          "&:hover": {
            backgroundColor: secondary,
            filter: "brightness(1.1)",
            transform: "translateY(-1px)",
          },
        },
        outlinedPrimary: {
          borderColor: primary,
          color: primary,
          "&:hover": { backgroundColor: dimPrimary, borderColor: primary },
        },
        outlinedSecondary: {
          borderColor: secondary,
          color: secondary,
          "&:hover": { backgroundColor: dimSecondary, borderColor: secondary },
        },
        text: {
          color: primary,
          "&:hover": { backgroundColor: dimPrimary },
        },
      },
      variants: [
        { props: { size: "small" }, style: { padding: "6px 14px", fontSize: "0.8rem", borderRadius: 6 } },
        { props: { size: "large" }, style: { padding: "13px 28px", fontSize: "1rem", borderRadius: 12 } },
      ],
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          background: grad,
          color: "#fff",
          boxShadow: shadowBrand,
          "&:hover": { background: gradHover },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "2px 8px",
          color: "rgba(255,255,255,0.65)",
          "&.Mui-selected": {
            background: `linear-gradient(90deg, ${secondary}30 0%, ${primary}30 100%)`,
            borderLeft: `3px solid ${secondary}`,
            color: "#fff",
            paddingLeft: "13px",
          },
          "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "#fff" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: "0.72rem", borderRadius: 20 },
        colorPrimary: { background: dimPrimary, color: primary, border: `1px solid ${primary}44` },
        colorSecondary: { background: dimSecondary, color: secondary, border: `1px solid ${secondary}44` },
        colorSuccess: { background: "#DCFCE7", color: "#15803D", border: "1px solid #BBF7D0" },
        colorWarning: { background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" },
        colorError: { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E6F0" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: primary },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: primary,
            borderWidth: "1.5px",
            boxShadow: `0 0 0 3px ${primary}22`,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#EF4444" },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#5A5F7A",
          fontWeight: 500,
          "&.Mui-focused": { color: primary },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#fff",
            "& + .MuiSwitch-track": { background: grad, opacity: 1 },
          },
        },
        track: { backgroundColor: "#C8CCDC", opacity: 1 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            backgroundColor: "#F4F6FB",
            color: "#5A5F7A",
            fontWeight: 700,
            fontSize: "0.78rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            borderBottom: "1px solid #E2E6F0",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: "#F8F9FD" },
          "&:last-child td": { borderBottom: "none" },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        standardInfo: {
          background: `linear-gradient(90deg, ${dimSecondary}, ${dimPrimary})`,
          color: primary,
          borderLeft: `3px solid ${primary}`,
        },
        standardSuccess: { background: "#F0FDF4", borderLeft: "3px solid #22C55E" },
        standardWarning: { background: "#FFFBEB", borderLeft: "3px solid #F59E0B" },
        standardError: { background: "#FFF1F2", borderLeft: "3px solid #EF4444" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9rem",
          color: "#9DA3BF",
          "&.Mui-selected": { color: primary },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { background: grad, height: 3, borderRadius: "3px 3px 0 0" },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: navBg,
          color: "rgba(255,255,255,0.85)",
          fontSize: "0.76rem",
          fontWeight: 500,
          borderRadius: 8,
          padding: "7px 12px",
          border: "1px solid rgba(255,255,255,0.08)",
        },
        arrow: { color: navBg },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: "#E2E6F0" } },
    },
    MuiAvatar: {
      styleOverrides: {
        colorDefault: { background: grad, color: "#fff", fontWeight: 700 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6, backgroundColor: dimSecondary },
        barColorPrimary: { background: grad },
      },
    },
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// 1. MERUBOT (original — Cyan × Purple)
// ══════════════════════════════════════════════════════════════════════════════
export const meruBotTheme = createTheme({
  ...shared,
  palette: {
    mode: "light",
    background: { default: "#F4F6FB", paper: "#FFFFFF", grad: "linear-gradient(135deg, #e1f0fa 0%, #c7ebfe 50%, #9adaf7 100%)"},
    primary: { main: "#8B5CF6", light: "#A78BFA", dark: "#6D35D9", contrastText: "#FFFFFF" },
    secondary: { main: "#00C8FF", light: "#67E8F9", dark: "#009AC4", contrastText: "#003040" },
    info: { main: "#00C8FF", light: "#E0F8FF", dark: "#009AC4" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error: { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: { primary: "#0F1023", secondary: "#5A5F7A", disabled: "#9DA3BF" },
    divider: "#E2E6F0",
  },
  typography: {
    ...shared.typography,
    h3: { fontWeight: 800, color: "#0F1023", letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, color: "#0F1023", letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, color: "#0F1023" },
    h6: { fontWeight: 600, color: "#0F1023" },
    subtitle1: { fontWeight: 600, color: "#5A5F7A" },
    body1: { fontSize: "0.95rem", color: "#0F1023" },
    body2: { fontSize: "0.875rem", color: "#5A5F7A" },
    caption: { fontSize: "0.75rem", color: "#9DA3BF" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary: "#8B5CF6",
    secondary: "#00C8FF",
    grad: "linear-gradient(135deg, #00C8FF 0%, #8B5CF6 100%)",
    gradHover: "linear-gradient(315deg, #00C8FF 0%, #8B5CF6 100%)",
    shadowBrand: "0 4px 20px rgba(0,200,255,0.25), 0 2px 12px rgba(139,92,246,0.20)",
    navBg: "#0D0F1A",
    dimPrimary: "#F0EBFF",
    dimSecondary: "#E0F8FF",
  }),
});

export const meruBotDarkTheme = createTheme({
  ...shared,
  palette: {
    mode: "dark",
    background: { 
      default: "#0D0F1A", // Deep space navy
      paper: "#141628"    // Slightly lighter card background
    },
    primary: { 
      main: "#8B5CF6", 
      light: "#C4B5FD", 
      dark: "#7C3AED", 
      contrastText: "#FFFFFF" 
    },
    secondary: { 
      main: "#00C8FF", 
      light: "#67E8F9", 
      dark: "#0891B2", 
      contrastText: "#FFFFFF" 
    },
    text: { 
      primary: "#F0F2FF",   // Almost white
      secondary: "#9DA3BF", // Muted gray-blue
      disabled: "#5A5F7A" 
    },
    divider: alpha("#E2E6F0", 0.1), // Subtle dark divider
  },
  typography: {
    ...shared.typography,
    // Note: We use the light colors here so they pop against the dark background
    h3: { fontWeight: 800, color: "#F0F2FF", letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, color: "#F0F2FF", letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, color: "#F0F2FF" },
    h6: { fontWeight: 600, color: "#F0F2FF" },
    subtitle1: { fontWeight: 600, color: "#9DA3BF" },
    body1: { fontSize: "0.95rem", color: "#F0F2FF" },
    body2: { fontSize: "0.875rem", color: "#9DA3BF" },
    caption: { fontSize: "0.75rem", color: "#5A5F7A" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary: "#8B5CF6",
    secondary: "#00C8FF",
    grad: "linear-gradient(135deg, #00C8FF 0%, #8B5CF6 100%)",
    gradHover: "linear-gradient(315deg, #00C8FF 0%, #8B5CF6 100%)",
    shadowBrand: "0 4px 25px rgba(0, 0, 0, 0.5)", // Darker shadow for dark mode
    navBg: "#090B14",
    dimPrimary: alpha("#8B5CF6", 0.15), // Uses alpha for subtle glows
    dimSecondary: alpha("#00C8FF", 0.15),
  }),
});

// ══════════════════════════════════════════════════════════════════════════════
// 2. MIDNIGHT EMBER (Deep Navy × Orange-Red)
// ══════════════════════════════════════════════════════════════════════════════
export const midnightEmberTheme = createTheme({
  ...shared,
  palette: {
    mode: "light",
    background: { default: "#FBF7F4", paper: "#FFFFFF" },
    primary: { main: "#EA580C", light: "#FB923C", dark: "#C2410C", contrastText: "#FFFFFF" },
    secondary: { main: "#1E3A5F", light: "#2D5D99", dark: "#0F2440", contrastText: "#FFFFFF" },
    info: { main: "#3B82F6", light: "#DBEAFE", dark: "#1D4ED8" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error: { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: { primary: "#1A0F08", secondary: "#6B5B52", disabled: "#B5A99F" },
    divider: "#EDE5DF",
  },
  typography: {
    ...shared.typography,
    h3: { fontWeight: 800, color: "#1A0F08", letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, color: "#1A0F08", letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, color: "#1A0F08" },
    h6: { fontWeight: 600, color: "#1A0F08" },
    subtitle1: { fontWeight: 600, color: "#6B5B52" },
    body1: { fontSize: "0.95rem", color: "#1A0F08" },
    body2: { fontSize: "0.875rem", color: "#6B5B52" },
    caption: { fontSize: "0.75rem", color: "#B5A99F" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary: "#EA580C",
    secondary: "#F97316",
    grad: "linear-gradient(135deg, #F97316 0%, #EF4444 100%)",
    gradHover: "linear-gradient(315deg, #F97316 0%, #EF4444 100%)",
    shadowBrand: "0 4px 20px rgba(249,115,22,0.30), 0 2px 12px rgba(239,68,68,0.20)",
    navBg: "#1E3A5F",
    dimPrimary: "#FFF7ED",
    dimSecondary: "#FFEDD5",
  }),
});

export const midnightEmberDarkTheme = createTheme({
  ...shared,
  palette: {
    mode: "dark",
    background: {
      default: "#0F0A07",  // Very dark warm navy/brown
      paper:   "#1A1208",  // Slightly lighter card background
    },
    primary: {
      main:         "#FB923C",  // Lighter orange for dark bg contrast
      light:        "#FDBA74",
      dark:         "#EA580C",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main:         "#2D5D99",
      light:        "#60A5FA",
      dark:         "#1E3A5F",
      contrastText: "#FFFFFF",
    },
    info:    { main: "#3B82F6", light: "#DBEAFE", dark: "#1D4ED8" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error:   { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: {
      primary:   "#FDF0E8",  // Warm off-white
      secondary: "#B5917A",  // Muted warm gray
      disabled:  "#6B5B52",
    },
    divider: alpha("#EDE5DF", 0.1),
  },
  typography: {
    ...shared.typography,
    h3:       { fontWeight: 800, color: "#FDF0E8", letterSpacing: "-0.02em" },
    h4:       { fontWeight: 700, color: "#FDF0E8", letterSpacing: "-0.01em" },
    h5:       { fontWeight: 700, color: "#FDF0E8" },
    h6:       { fontWeight: 600, color: "#FDF0E8" },
    subtitle1:{ fontWeight: 600, color: "#B5917A" },
    body1:    { fontSize: "0.95rem",   color: "#FDF0E8" },
    body2:    { fontSize: "0.875rem",  color: "#B5917A" },
    caption:  { fontSize: "0.75rem",   color: "#6B5B52" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary:     "#FB923C",
    secondary:   "#F97316",
    grad:        "linear-gradient(135deg, #F97316 0%, #EF4444 100%)",
    gradHover:   "linear-gradient(315deg, #F97316 0%, #EF4444 100%)",
    shadowBrand: "0 4px 25px rgba(0, 0, 0, 0.6)",
    navBg:       "#090B14",
    dimPrimary:  alpha("#EA580C", 0.15),
    dimSecondary:alpha("#F97316", 0.15),
  }),
});


// ══════════════════════════════════════════════════════════════════════════════
// 3. FOREST SAGE (Deep Green × Lime)
// ══════════════════════════════════════════════════════════════════════════════
export const forestSageTheme = createTheme({
  ...shared,
  palette: {
    mode: "light",
    background: { default: "#F4FBF6", paper: "#FFFFFF" },
    primary: { main: "#16A34A", light: "#4ADE80", dark: "#14532D", contrastText: "#FFFFFF" },
    secondary: { main: "#84CC16", light: "#BEF264", dark: "#3F6212", contrastText: "#1A2E05" },
    info: { main: "#0EA5E9", light: "#E0F2FE", dark: "#0369A1" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error: { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: { primary: "#0A1F10", secondary: "#4A6052", disabled: "#93B09A" },
    divider: "#D1E8D6",
  },
  typography: {
    ...shared.typography,
    h3: { fontWeight: 800, color: "#0A1F10", letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, color: "#0A1F10", letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, color: "#0A1F10" },
    h6: { fontWeight: 600, color: "#0A1F10" },
    subtitle1: { fontWeight: 600, color: "#4A6052" },
    body1: { fontSize: "0.95rem", color: "#0A1F10" },
    body2: { fontSize: "0.875rem", color: "#4A6052" },
    caption: { fontSize: "0.75rem", color: "#93B09A" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary: "#16A34A",
    secondary: "#84CC16",
    grad: "linear-gradient(135deg, #22C55E 0%, #84CC16 100%)",
    gradHover: "linear-gradient(315deg, #22C55E 0%, #84CC16 100%)",
    shadowBrand: "0 4px 20px rgba(34,197,94,0.28), 0 2px 12px rgba(132,204,22,0.22)",
    navBg: "#14532D",
    dimPrimary: "#DCFCE7",
    dimSecondary: "#ECFCCB",
  }),
});

export const forestSageDarkTheme = createTheme({
  ...shared,
  palette: {
    mode: "dark",
    background: {
      default: "#060F09",  // Very dark forest green
      paper:   "#0D1A10",  // Slightly lighter card background
    },
    primary: {
      main:         "#4ADE80",  // Lighter green for dark bg contrast
      light:        "#86EFAC",
      dark:         "#16A34A",
      contrastText: "#021005",
    },
    secondary: {
      main:         "#A3E635",  // Bright lime
      light:        "#D9F99D",
      dark:         "#84CC16",
      contrastText: "#1A2E05",
    },
    info:    { main: "#0EA5E9", light: "#E0F2FE", dark: "#0369A1" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error:   { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: {
      primary:   "#ECFDF5",  // Very light mint-white
      secondary: "#6EBF87",  // Muted sage green
      disabled:  "#3A6048",
    },
    divider: alpha("#D1E8D6", 0.1),
  },
  typography: {
    ...shared.typography,
    h3:       { fontWeight: 800, color: "#ECFDF5", letterSpacing: "-0.02em" },
    h4:       { fontWeight: 700, color: "#ECFDF5", letterSpacing: "-0.01em" },
    h5:       { fontWeight: 700, color: "#ECFDF5" },
    h6:       { fontWeight: 600, color: "#ECFDF5" },
    subtitle1:{ fontWeight: 600, color: "#6EBF87" },
    body1:    { fontSize: "0.95rem",  color: "#ECFDF5" },
    body2:    { fontSize: "0.875rem", color: "#6EBF87" },
    caption:  { fontSize: "0.75rem",  color: "#3A6048" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary:     "#4ADE80",
    secondary:   "#A3E635",
    grad:        "linear-gradient(135deg, #22C55E 0%, #84CC16 100%)",
    gradHover:   "linear-gradient(315deg, #22C55E 0%, #84CC16 100%)",
    shadowBrand: "0 4px 25px rgba(0, 0, 0, 0.6)",
    navBg:       "#030A05",
    dimPrimary:  alpha("#16A34A", 0.15),
    dimSecondary:alpha("#84CC16", 0.15),
  }),
});

// ══════════════════════════════════════════════════════════════════════════════
// 4. ROSE QUARTZ (Mauve × Pink-Purple)
// ══════════════════════════════════════════════════════════════════════════════
export const roseQuartzTheme = createTheme({
  ...shared,
  palette: {
    mode: "light",
    background: { default: "#FDF4F8", paper: "#FFFFFF" },
    primary: { main: "#DB2777", light: "#F472B6", dark: "#9D174D", contrastText: "#FFFFFF" },
    secondary: { main: "#A855F7", light: "#D8B4FE", dark: "#7E22CE", contrastText: "#FFFFFF" },
    info: { main: "#3B82F6", light: "#DBEAFE", dark: "#1D4ED8" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error: { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: { primary: "#1F0B14", secondary: "#6E4D5C", disabled: "#C4A0B3" },
    divider: "#F0D9E6",
  },
  typography: {
    ...shared.typography,
    h3: { fontWeight: 800, color: "#1F0B14", letterSpacing: "-0.02em" },
    h4: { fontWeight: 700, color: "#1F0B14", letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, color: "#1F0B14" },
    h6: { fontWeight: 600, color: "#1F0B14" },
    subtitle1: { fontWeight: 600, color: "#6E4D5C" },
    body1: { fontSize: "0.95rem", color: "#1F0B14" },
    body2: { fontSize: "0.875rem", color: "#6E4D5C" },
    caption: { fontSize: "0.75rem", color: "#C4A0B3" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary: "#DB2777",
    secondary: "#A855F7",
    grad: "linear-gradient(135deg, #F472B6 0%, #A855F7 100%)",
    gradHover: "linear-gradient(315deg, #F472B6 0%, #A855F7 100%)",
    shadowBrand: "0 4px 20px rgba(244,114,182,0.28), 0 2px 12px rgba(168,85,247,0.22)",
    navBg: "#4A1942",
    dimPrimary: "#FCE7F3",
    dimSecondary: "#F5F3FF",
  }),
});

export const roseQuartzDarkTheme = createTheme({
  ...shared,
  palette: {
    mode: "dark",
    background: {
      default: "#100710",  // Very dark purple-black
      paper:   "#1A0D1A",  // Slightly lighter card background
    },
    primary: {
      main:         "#F472B6",  // Lighter pink for dark bg contrast
      light:        "#FBCFE8",
      dark:         "#DB2777",
      contrastText: "#1F0B14",
    },
    secondary: {
      main:         "#C084FC",  // Lighter purple
      light:        "#E9D5FF",
      dark:         "#A855F7",
      contrastText: "#FFFFFF",
    },
    info:    { main: "#3B82F6", light: "#DBEAFE", dark: "#1D4ED8" },
    success: { main: "#22C55E", light: "#86EFAC", dark: "#15803D" },
    warning: { main: "#F59E0B", light: "#FDE68A", dark: "#92400E" },
    error:   { main: "#EF4444", light: "#FECACA", dark: "#991B1B" },
    text: {
      primary:   "#FDF2F8",  // Very light pink-white
      secondary: "#C084A0",  // Muted mauve
      disabled:  "#7A4060",
    },
    divider: alpha("#F0D9E6", 0.1),
  },
  typography: {
    ...shared.typography,
    h3:       { fontWeight: 800, color: "#FDF2F8", letterSpacing: "-0.02em" },
    h4:       { fontWeight: 700, color: "#FDF2F8", letterSpacing: "-0.01em" },
    h5:       { fontWeight: 700, color: "#FDF2F8" },
    h6:       { fontWeight: 600, color: "#FDF2F8" },
    subtitle1:{ fontWeight: 600, color: "#C084A0" },
    body1:    { fontSize: "0.95rem",  color: "#FDF2F8" },
    body2:    { fontSize: "0.875rem", color: "#C084A0" },
    caption:  { fontSize: "0.75rem",  color: "#7A4060" },
  },
  shape: shared.shape,
  components: buildComponents({
    primary:     "#F472B6",
    secondary:   "#C084FC",
    grad:        "linear-gradient(135deg, #F472B6 0%, #A855F7 100%)",
    gradHover:   "linear-gradient(315deg, #F472B6 0%, #A855F7 100%)",
    shadowBrand: "0 4px 25px rgba(0, 0, 0, 0.6)",
    navBg:       "#0A040A",
    dimPrimary:  alpha("#DB2777", 0.15),
    dimSecondary:alpha("#A855F7", 0.15),
  }),
});


// ── Theme Registry ────────────────────────────────────────────────────────────
export const THEMES = {
  merubot:       { label: "MeruBot",        light: meruBotTheme,       dark: meruBotDarkTheme       },
  midnightEmber: { label: "Midnight Ember", light: midnightEmberTheme, dark: midnightEmberDarkTheme },
  forestSage:    { label: "Forest Sage",    light: forestSageTheme,    dark: forestSageDarkTheme    },
  roseQuartz:    { label: "Rose Quartz",    light: roseQuartzTheme,    dark: roseQuartzDarkTheme    },
};